import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

const nameSchema = z.object({ name: z.string().trim().min(1, 'Name is required').max(255) });
type NameForm = z.infer<typeof nameSchema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  label: string;
  submitLabel: string;
  initialValue?: string;
  isPending?: boolean;
  testId: string;
  // Throws on failure — the parent surfaces the toast; the dialog stays open.
  onSubmit: (name: string) => Promise<void>;
}

// Single-field name form reused for add-site, add-well, and rename. Uses the
// repo's react-hook-form + zod + shadcn Form* convention.
export function NameFormDialog({
  open,
  onOpenChange,
  title,
  description,
  label,
  submitLabel,
  initialValue = '',
  isPending,
  testId,
  onSubmit,
}: Props) {
  const form = useForm<NameForm>({
    resolver: zodResolver(nameSchema),
    defaultValues: { name: initialValue },
  });

  // Reset to the current initial value each time the dialog opens.
  useEffect(() => {
    if (open) form.reset({ name: initialValue });
  }, [open, initialValue, form]);

  const submit = form.handleSubmit(async ({ name }) => {
    try {
      await onSubmit(name);
      onOpenChange(false);
    } catch {
      // Parent toasts the error; keep the dialog open so the admin can retry.
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{label}</FormLabel>
                  <FormControl>
                    <Input data-testid={`${testId}-name`} autoFocus {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid={`${testId}-submit`} disabled={isPending}>
                {isPending ? 'Saving…' : submitLabel}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
